import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    EnterpriseSubscription,
    EnterpriseSubscriptionModuleDefinition,
    EnterpriseSubscriptionPlanDefinition,
    CompanyPlanCode,
    CompanyPlanDefinition,
    CompanyPlanSubscription,
    CompanyPlanUsage,
    MarkTrialOnboardingChecklistItemRequest,
    TrialOnboardingStartRequest,
    TrialOnboardingStartResponse,
    TrialOnboardingChecklistItem,
    TrialOnboardingWorkspaceResponse,
    TrialLifecycleResponse,
    TrialOnboardingMeasurementEventRequest,
    TrialOnboardingMeasurementEventResponse,
    TrialOnboardingProvisioningSummary,
    TrialOnboardingRecoveryResumeResponse
} from "./enterprise-subscription.model";
import { environment } from "../../environments/environment";
import { APIResponseWrapper } from "../models/common/api-response-wrapper";


@Injectable({
    providedIn: 'root'
})
export class EnterpriseSubscriptionService {

    private apiUrl = `${environment.restApiUrl}enterprise-subscriptions`; // Base URL
    private paymentApiUrl = `${environment.restApiUrl}enterprise-subscription`;
    private trialOnboardingApiUrl = `${environment.restApiUrl}trial-onboarding`;
    private companyPlanApiUrl = `${environment.restApiUrl}company-plans`;

    constructor(private http: HttpClient) { }

    createSubscription(subscription: EnterpriseSubscription): Observable<EnterpriseSubscription> {
        return this.http.post<EnterpriseSubscription>(this.apiUrl, subscription);
    }

    createPaymentLink(subscription: EnterpriseSubscription): Observable<any> {
        return this.http.post<any>(`${this.paymentApiUrl}/generate-payment-link`, subscription);
    }

    startTrialOnboarding(request: TrialOnboardingStartRequest): Observable<APIResponseWrapper<TrialOnboardingStartResponse>> {
        return this.http.post<APIResponseWrapper<TrialOnboardingStartResponse>>(`${this.trialOnboardingApiUrl}/start`, request);
    }

    getTrialOnboardingWorkspace(companyId: number): Observable<APIResponseWrapper<TrialOnboardingWorkspaceResponse>> {
        return this.http.get<APIResponseWrapper<TrialOnboardingWorkspaceResponse>>(`${this.trialOnboardingApiUrl}/workspace/${companyId}`);
    }

    markTrialOnboardingChecklistItem(companyId: number, itemKey: string, request: MarkTrialOnboardingChecklistItemRequest): Observable<APIResponseWrapper<TrialOnboardingChecklistItem>> {
        return this.http.post<APIResponseWrapper<TrialOnboardingChecklistItem>>(`${this.trialOnboardingApiUrl}/workspace/${companyId}/checklist/${itemKey}`, request);
    }

    getTrialLifecycle(companyId: number): Observable<APIResponseWrapper<TrialLifecycleResponse>> {
        return this.http.get<APIResponseWrapper<TrialLifecycleResponse>>(`${this.trialOnboardingApiUrl}/lifecycle/${companyId}`);
    }

    recordTrialOnboardingEvent(request: TrialOnboardingMeasurementEventRequest): Observable<APIResponseWrapper<TrialOnboardingMeasurementEventResponse>> {
        return this.http.post<APIResponseWrapper<TrialOnboardingMeasurementEventResponse>>(`${this.trialOnboardingApiUrl}/events`, request);
    }

    getTrialProvisioningSummary(companyId: number): Observable<APIResponseWrapper<TrialOnboardingProvisioningSummary>> {
        return this.http.get<APIResponseWrapper<TrialOnboardingProvisioningSummary>>(`${this.trialOnboardingApiUrl}/provisioning/${companyId}`);
    }

    resumeTrialOnboarding(token: string): Observable<APIResponseWrapper<TrialOnboardingRecoveryResumeResponse>> {
        return this.http.post<APIResponseWrapper<TrialOnboardingRecoveryResumeResponse>>(`${this.trialOnboardingApiUrl}/recovery/${token}/resume`, {});
    }

    createPaymentLinkForSavedSubscription(subscriptionId: number): Observable<any> {
        return this.http.post<any>(`${this.paymentApiUrl}/generate-payment-link/${subscriptionId}`, {});
    }

    getSubscription(id: number): Observable<EnterpriseSubscription> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.get<EnterpriseSubscription>(url);
    }

    getSubscriptionsByCompanyId(companyId: number): Observable<EnterpriseSubscription[]> {
        const url = `${this.apiUrl}/company/${companyId}`;
        return this.http.get<EnterpriseSubscription[]>(url);
    }

    getPlanCatalog(): Observable<EnterpriseSubscriptionPlanDefinition[]> {
        return this.http.get<EnterpriseSubscriptionPlanDefinition[]>(`${this.apiUrl}/plan-catalog`);
    }

    getCompanyPlanCatalog(): Observable<CompanyPlanDefinition[]> {
        return this.http.get<CompanyPlanDefinition[]>(`${this.companyPlanApiUrl}/catalog`);
    }

    getEffectiveCompanyPlan(companyId: number): Observable<CompanyPlanSubscription> {
        return this.http.get<CompanyPlanSubscription>(`${this.companyPlanApiUrl}/company/${companyId}`);
    }

    getCompanyPlanUsage(companyId: number): Observable<CompanyPlanUsage> {
        return this.http.get<CompanyPlanUsage>(`${this.companyPlanApiUrl}/company/${companyId}/usage`);
    }

    activateFreePlan(companyId: number): Observable<CompanyPlanSubscription> {
        return this.http.post<CompanyPlanSubscription>(`${this.companyPlanApiUrl}/company/${companyId}/free`, {});
    }

    startCompanyPlanTrial(companyId: number, planCode: CompanyPlanCode): Observable<CompanyPlanSubscription> {
        return this.http.post<CompanyPlanSubscription>(`${this.companyPlanApiUrl}/company/${companyId}/trial/${planCode}`, {});
    }

    checkoutCompanyPlan(companyId: number, planCode: CompanyPlanCode): Observable<APIResponseWrapper<string>> {
        return this.http.post<APIResponseWrapper<string>>(`${this.companyPlanApiUrl}/company/${companyId}/checkout/${planCode}`, {});
    }

    getModuleCatalog(): Observable<EnterpriseSubscriptionModuleDefinition[]> {
        return this.http.get<EnterpriseSubscriptionModuleDefinition[]>(`${this.apiUrl}/module-catalog`);
    }

    hasActiveSubscription(companyId: number, module: string): Observable<boolean> {
        return this.http.get<boolean>(`${this.apiUrl}/company/${companyId}/module/${module}/active`);
    }

    getAllSubscriptions(): Observable<EnterpriseSubscription[]> {
        return this.http.get<EnterpriseSubscription[]>(this.apiUrl);
    }

    updateSubscription(id: number, subscription: EnterpriseSubscription): Observable<EnterpriseSubscription> {
        const url = `${this.apiUrl}/${id}`;
        return this.http.put<EnterpriseSubscription>(url, subscription);
    }

    deleteSubscription(id: number): Observable<HttpResponse<any>> {  // Use HttpResponse<any>
        const url = `${this.apiUrl}/${id}`;
        return this.http.delete(url, { observe: 'response' }); // Observe the full response
    }
}
